import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import Badge, { BadgeTone } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const ORDER_STATUS_TONE: Record<string, BadgeTone> = {
  issued: 'info',
  accepted: 'warning',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'neutral',
};

const ContractorPortal: React.FC = () => {
  const [contractors, setContractors] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [contractorId, setContractorId] = useState('');
  const [error, setError] = useState('');
  const [performance, setPerformance] = useState<any>(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const load = async () => {
    try {
      const [c, w]: any[] = await Promise.all([
        apiService.get('/platform/contractors'), apiService.get('/portal/contractor/work-orders')
      ]);
      setContractors(c.data || []);
      setOrders(w.data || []);
      setContractorId(id => id || c.data?.[0]?.id || '');
    } catch (e: any) { setError(e.response?.data?.error?.message || 'Unable to load contractor work.'); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!contractorId) { setPerformance(null); return; }
    setPerformanceLoading(true);
    apiService.get<any>(`/portal/contractor/performance?contractorId=${contractorId}`)
      .then((r: any) => setPerformance(r.data || null))
      .catch(() => setPerformance(null))
      .finally(() => setPerformanceLoading(false));
  }, [contractorId]);
  const visible = useMemo(() => orders.filter(o => !contractorId || o.contractor_id === contractorId), [orders, contractorId]);
  const update = async (order: any, status: string) => {
    try {
      await apiService.patch(`/portal/contractor/work-orders/${order.id}/status`, { status });
      await load();
    } catch (e: any) { setError(e.response?.data?.error?.message || 'Unable to update work order.'); }
  };
  return (
    <div>
      <PageHeader title="Contractor Work Portal" subtitle="Issue, track and acknowledge outsourced municipal work." />
      {error && <div className="mb-4 rounded-sm border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
      <label className="mb-4 block max-w-lg text-sm font-medium text-gray-700 dark:text-gray-300">Contractor
        <select value={contractorId} onChange={e => setContractorId(e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 p-2">
          <option value="">All contractors</option>{contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>

      {/* Own SLA contract terms, rating, breaches and escalations —
          previously this only surfaced to municipality admins. */}
      {contractorId && (
        <div className="mb-6 rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-3 text-lg font-semibold">Your performance</h2>
          {performanceLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !performance ? (
            <p className="text-sm text-gray-500">No performance data available yet.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Rating</div>
                  <div className="text-xl font-semibold">{performance.contractor?.rating ?? '—'}</div>
                </div>
                <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total jobs</div>
                  <div className="text-xl font-semibold">{performance.totalAssignments}</div>
                </div>
                <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Breaches</div>
                  <div className="text-xl font-semibold text-red-600">{performance.breachCount}</div>
                </div>
                <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Breach rate</div>
                  <div className="text-xl font-semibold">{((performance.breachRate || 0) * 100).toFixed(1)}%</div>
                </div>
              </div>

              {performance.escalations?.some((e: any) => e.status === 'open') && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-medium text-red-800">You have an open escalation</p>
                  {performance.escalations.filter((e: any) => e.status === 'open').map((e: any) => (
                    <p key={e.id} className="mt-1 text-xs text-red-700">{e.reason}</p>
                  ))}
                </div>
              )}

              <div>
                <h3 className="mb-2 text-sm font-semibold">Your contract terms</h3>
                {(!performance.contracts || performance.contracts.length === 0) ? (
                  <p className="text-sm text-gray-500">No contracts on file yet.</p>
                ) : (
                  <div className="space-y-2">
                    {performance.contracts.map((c: any) => (
                      <div key={c.id} className="flex items-center justify-between rounded-sm border border-gray-200 px-3 py-2 text-sm dark:border-gray-700">
                        <span className="font-medium">{c.name}</span>
                        <span className="text-gray-500 dark:text-gray-400">
                          response {c.response_minutes}m · resolution {c.resolution_minutes}m · escalate after {c.breach_threshold} breaches / {c.breach_window_days}d
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold">Recent breaches</h3>
                {(!performance.recentBreaches || performance.recentBreaches.length === 0) ? (
                  <p className="text-sm text-gray-500">No breaches recorded.</p>
                ) : (
                  <div className="space-y-1">
                    {performance.recentBreaches.map((b: any) => (
                      <div key={b.id + b.breach_type} className="text-sm text-gray-700 dark:text-gray-300">
                        {b.breach_type === 'response' ? 'Response' : 'Resolution'} breach — {b.report_title || b.report_number || 'report'} (due {new Date(b.due_at).toLocaleString()})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState icon={ClipboardDocumentListIcon} title="No work orders" description="No work orders for this contractor." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {visible.map(o => (
            <article key={o.id} className="rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-primary-700">{o.work_order_number}</p>
                  <h2 className="text-lg font-semibold">{o.title}</h2>
                </div>
                <Badge tone={ORDER_STATUS_TONE[o.status] ?? 'neutral'}>{o.status?.replaceAll('_', ' ')}</Badge>
              </div>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">{o.scope}</p>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-gray-500 dark:text-gray-400">Approved value</dt><dd className="font-medium">R {Number(o.approved_amount || 0).toLocaleString()}</dd></div>
                <div><dt className="text-gray-500 dark:text-gray-400">Due</dt><dd className="font-medium">{o.due_at ? new Date(o.due_at).toLocaleDateString() : 'Not set'}</dd></div>
              </dl>
              <div className="mt-4 flex flex-wrap gap-2">
                {o.status === 'issued' && <Button variant="primary" size="sm" onClick={() => update(o, 'accepted')}>Accept work</Button>}
                {o.status === 'accepted' && <Button variant="primary" size="sm" onClick={() => update(o, 'in_progress')}>Start work</Button>}
                {o.status === 'in_progress' && (
                  <Button size="sm" className="border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800" onClick={() => update(o, 'completed')}>
                    Submit complete
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorPortal;
